import i18next from "i18next";
import { html } from "lit-html";
import { getTenantLabel } from "GlobalShared/helpers/tenantHelper";
const priceLine = (label, value, tooltip) => {
  return html`<div class="flex justify-between text-gray-600">
		<div>${label} ${tooltip !== void 0 ? html`<dc-tooltip .label=${tooltip}></dc-tooltip>` : ""}</div>
		<div class="whitespace-nowrap">&euro; ${value}</div>
	</div>`;
};
export const priceBreakdownTemplate = (props) => {
  if (props === void 0 || props.summary === void 0) {
    return html``;
  }
  const fiscalRepPriceLineTemplate = (country) => country.fiscalRep > 0 || country.optionalFiscalRep > 0 ? priceLine(
    i18next.t("Fiscal representation"),
    country.fiscalRep > 0 ? country.fiscalRep : country.optionalFiscalRep,
    i18next.t("When operating in a country where fiscal representation is required, you must appoint the Fiscal Representative provided by {{tenantLabel}}.", {
      tenantLabel: getTenantLabel(props.tenantName)
    })
  ) : "";
  const filingAgentPriceLineTemplate = (country) => country.filingAgent > 0 ? priceLine(i18next.t("Filing agent"), country.filingAgent) : "";
  const intrastatPriceLineTemplate = (country) => country.intrastat > 0 ? priceLine(i18next.t("Intrastat Report"), country.intrastat) : "";
  const homeCountryCompliancePriceLineTemplate = (country) => country.homeCountryCompliance > 0 ? priceLine(i18next.t("Home country compliance"), country.homeCountryCompliance) : "";
  const monthlyCompliancePriceLineTemplate = (country) => country.monthlyCompliance > 0 ? priceLine(i18next.t("Monthly country compliance"), country.monthlyCompliance) : "";
  const selfRevisionPriceLineTemplate = (country) => country.selfRevision > 0 ? priceLine(i18next.t("Self Revision / Retrospective Returns"), country.selfRevision) : "";
  const deregistrationPriceLineTemplate = (country) => country.deregistration > 0 ? priceLine(i18next.t("Deregistration"), country.deregistration) : "";
  const countryRetroTemplate = (country) => country.retro > 0 ? country.label === "Italy" ? priceLine(
    i18next.t("Retrospective Return"),
    country.retro,
    i18next.t(
      "The calculation of the retrospective periods is crucial for preparation of the registration documents in Italy. You must provide {{tenantLabel}} your transaction data covering past reporting periods as soon as possible. Without them the Italian VAT registration process might be delayed.",
      { tenantLabel: getTenantLabel(props.tenantName) }
    )
  ) : priceLine(i18next.t("Retrospective Return"), country.retro) : "";
  const summaryPriceLinesByCountryTemplate = () => props.summary.countries ? props.summary.countries.filter((c) => c.total > 0).map(
    (country) => html`
							<div class="py-2">
								${priceLine(country.label, country.total)}

								<div class="pl-4">
									${fiscalRepPriceLineTemplate(country)} ${filingAgentPriceLineTemplate(country)} ${countryRetroTemplate(country)} ${intrastatPriceLineTemplate(country)}
									${homeCountryCompliancePriceLineTemplate(country)} ${monthlyCompliancePriceLineTemplate(country)} ${selfRevisionPriceLineTemplate(country)}
									${deregistrationPriceLineTemplate(country)}
								</div>
							</div>
						`
  ) : "";
  const eoriPriceLineTemplate = () => props.summary.eori ? html`<div class="py-2">${priceLine(i18next.t("EORI number application in the EU"), props.summary.eori)}</div>` : "";
  const ukEoriPriceLineTemplate = () => props.summary.ukEori ? html`<div class="py-2">${priceLine(i18next.t("EORI number application in the UK"), props.summary.ukEori)}</div>` : "";
  const iossPriceLineTemplate = () => props.summary.ioss ? html`
					<div class="py-2">
						${priceLine(
    i18next.t("IOSS Registration and Compliance"),
    props.summary.ioss,
    props.summary.isIossTier0 ? i18next.t("This is the IOSS registration fee only. Monthly compliance fees will be charged separately. Detailed instructions will follow") : i18next.t("Total fee includes the registration fee and the monthly compliance fees up to the selected package amount.")
  )}
					</div>
			  ` : "";
  const iossDeregistrationPriceLineTemplate = () => props.summary.iossDeregistration ? html`<div class="py-2">${priceLine(i18next.t("IOSS Deregistration"), props.summary.iossDeregistration)}</div>` : "";
  const ossYearlyCompliancePriceLineTemplate = () => props.summary.ossSummary.yearlyCompliance ? html`<div class="py-2">${priceLine(i18next.t("OSS Registration and Compliance"), props.summary.ossSummary.yearlyCompliance)}</div>` : "";
  const ossQuarterlyCompliancePriceLineTemplate = () => props.summary.ossSummary.quarterlyCompliance ? html`<div class="py-2">${priceLine(i18next.t("OSS Quarterly Compliance"), props.summary.ossSummary.quarterlyCompliance)}</div>` : "";
  const ossDeregistrationPriceLineTemplate = () => props.summary.ossSummary.deregistration ? html`<div class="py-2">${priceLine(i18next.t("OSS Deregistration"), props.summary.ossSummary.deregistration)}</div>` : "";
  const changeCompanyDetailPriceLineTemplate = () => props.summary.changeCompanyDetail ? html`<div class="py-2">${priceLine(i18next.t("Change Company Detail"), props.summary.changeCompanyDetail)}</div>` : "";
  const generalVatAdvicePriceLineTemplate = () => props.summary.generalVatAdvice ? html`<div class="py-2">${priceLine(i18next.t("General VAT Advice"), props.summary.generalVatAdvice)}</div>` : "";
  const specialistVatAdvicePriceLineTemplate = () => props.summary.specialistVatAdvice ? html`<div class="py-2">${priceLine(i18next.t("Specialist VAT Advice"), props.summary.specialistVatAdvice)}</div>` : "";
  const communicatingWithTaxAuthoritiesPriceLineTemplate = () => props.summary.communicatingWithTaxAuthorities ? html`<div class="py-2">${priceLine(i18next.t("Communicating with Tax Authorities"), props.summary.communicatingWithTaxAuthorities)}</div>` : "";
  return html`
		<div>
			<div class="text-gray-600 text-lg pb-4">${i18next.t("Order Summary")}</div>
			<hr class="mb-4" />
			<div class="text-sm">
				${summaryPriceLinesByCountryTemplate()} ${eoriPriceLineTemplate()} ${ukEoriPriceLineTemplate()} ${iossPriceLineTemplate()} ${iossDeregistrationPriceLineTemplate()}
				${ossYearlyCompliancePriceLineTemplate()} ${ossQuarterlyCompliancePriceLineTemplate()} ${ossDeregistrationPriceLineTemplate()} ${changeCompanyDetailPriceLineTemplate()}
				${generalVatAdvicePriceLineTemplate()} ${specialistVatAdvicePriceLineTemplate()} ${communicatingWithTaxAuthoritiesPriceLineTemplate()}
				<hr />
				<div class="py-4 text-gray-600">
					<div class="flex justify-between">
						<div>${i18next.t("Sub-total")}</div>
						<div>&euro; ${props.summary.subTotal}</div>
					</div>
					<div class="flex justify-between">
						<div>${i18next.t("VAT")}</div>
						<div>&euro; ${props.summary.vat}</div>
					</div>
				</div>
				<hr />
				<div class="flex justify-between font-bold py-4">
					<div>${i18next.t("Total fee")}</div>
					<div>&euro; ${props.summary.total}</div>
				</div>
			</div>
		</div>
	`;
};
